import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { obtenerCuotaMensual, amoutWithoutPrime, obtenerCuotaMinicuotas, getCampaingIfApplies } from '../constants';
import useVersion from './useVersion';

const useCar = car => {
  const { color } = useParams();
  const { hasActivePromo } = useVersion();
  const { selectedBank, selectedPrime, plazo, descuentoWeb } = useSelector(state => ({
    selectedBank: state?.personalization?.additional?.bancos?.find(
      bank => bank.isSelected
    ),
    selectedPrime: state.personalization?.additional?.prima,
    plazo: state.personalization?.additional?.plazo,
    descuentoWeb: state.personalization?.additional?.descuentoWeb,
  }));

  const hasCar = () => {
    return car?.codigo;
  };

  const getMaintenancePrice = () => {
    if (!hasCar()) {
      return 0;
    }
    return (car?.maintenances || []).reduce((acc, curr) => {
      if (curr.selected) {
        return acc + parseFloat(curr.precio);
      }
      return acc;
    }, 0);
  };

  const primeSecure = () => {
    const isNumber = function isNumber(value) 
    {
      return typeof value === 'number' && isFinite(value);
    }

    if (!hasCar()) {
      return 0;
    }
    const primaSeguro = parseFloat(car.primaSeguro);
    if (isNumber(primaSeguro)) {
      return primaSeguro;
    }
    return 0;
  }

  const getInsuarancePrice = () => {
    if (!hasCar()) {
      return 0;
    }
    return (car?.insurances || []).reduce((acc, curr) => {
      if (curr.selected) {
        return acc + parseFloat(curr.precio);
      }
      return acc;
    }, 0);
  };

  const getDefaultMinicuotaInsurance = () => {
    const originalSelected = getInsuarancePrice();
    if (originalSelected > 0) {
      return originalSelected;
    }

    return (car?.insurances || []).reduce((acc, curr) => {
      if (curr.nombre === 'QUALITAS') {
        return acc + parseFloat(curr.precio);
      }
      return acc;
    }, 0);
  };

  const getAccesoriesPrice = () => {
    if (!hasCar()) {
      return 0;
    }
    const accs = car?.accessories || {};

    return Object.keys(accs).reduce((acc, curr) => {
      return (
        acc +
        accs[curr].reduce((acc2, curr2) => {
          return curr2.isSelected ? acc2 + parseFloat(curr2.precioVenta) : acc2;
        }, 0)
      );
    }, 0);
  };

  // Declaration
  const maintenacePrice = getMaintenancePrice();
  const accsPrice = getAccesoriesPrice();
  const insurancePrice = getInsuarancePrice();

  const getCarPrice = () => {
    if (!hasCar()) {
      return 0;
    }
    
    return parseFloat(car?.precioOferta);
  }
  const getDescuentoFromCoupon = () => {
    if (!hasCar()) {
      return 0;
    }
    
    const compaingOverride = getCampaingIfApplies(car.modelo, car.codigoAnio)
    return parseFloat(compaingOverride?.price || 0);
  }

  const getPriceWithCoupon = () => {
    if (!hasCar()) {
      return false;
    }
    let descuentoGerencia = 0;
    let finalPrice = getCarPrice();
    if (car.descuentoAplicado && car.descuentoGerencia) {
      descuentoGerencia = parseFloat(car.descuentoGerencia);
      if (descuentoGerencia > 0) {
        finalPrice = finalPrice - descuentoGerencia;
      }
    }
    if (car.descuentoWebAplicado && (descuentoWeb || getDescuentoFromCoupon())) {
      const descuentoWebF = parseFloat(getDescuentoFromCoupon() || descuentoWeb.monto);
      finalPrice = finalPrice - descuentoWebF;
    }
    return finalPrice + accsPrice + maintenacePrice;
  };

  const hasDescuentoGerencia = () => {
    if (!hasCar()) {
      return false;
    }

    if (!!car.descuentoGerencia) {
      var descuentoGerencia = parseFloat(car.descuentoGerencia);
      if (descuentoGerencia > 0) {
        return true;
      }
    }

    return false;
  }

  const getFinalPrice = () => {
    if (!hasCar()) {
      return 0;
    }
    const carPrice = getCarPrice();
    return carPrice + accsPrice + maintenacePrice;
  };

  
  // Declaration
  const finalPrice = getFinalPrice();

  const getCuota = (hasActivePromo) => {
    if (!hasCar()) {
      return 0;
    }
    const prime = selectedPrime || 20;

    if (hasActivePromo) {
      return obtenerCuotaMinicuotas(finalPrice, amoutWithoutPrime(getCarPrice(), prime), true, getInsuarancePrice(), primeSecure(), getCarPrice());
    }
    const interes = selectedBank?.interes || 6.9;
    const plazoR = plazo || 96;

    return (
      obtenerCuotaMensual(
        interes,
        plazoR,
        amoutWithoutPrime(finalPrice, prime)
      ) + insurancePrice
    );
  };

  const selectedAccesories = () => {
    if (!hasCar()) return [];
    const keys = Object.keys(car?.accessories);
    return keys.reduce((acc, curr) => {
      return acc.concat(
        car.accessories[curr]
          .map(a => {
            if (a.isSelected) {
              return {
                category: curr,
                ...a,
              };
            }
            return null;
          })
          .filter(Boolean)
      );
    }, []);
  };

  const selectedMaintenance = () => {
    if (!hasCar()) return null;
    return car?.maintenances.reduce((acc, curr) => {
      if (acc) {
        return acc;
      }
      if (curr.selected) return curr;
      return null;
    }, null);
  };

  const selectedInsurance = () => {
    if (!hasCar()) return null;
    return car?.insurances.reduce((acc, curr) => {
      if (acc) {
        return acc;
      }
      if (curr.selected) return curr;
      return null;
    }, null);
  };

  const hasColor = () => {
    const colors = car?.colors || [];

    return colors.find(x => x.colorDesc.toLowerCase() === color?.toLowerCase());
  };

  const getImageUrl = () => {
    const pickedColor = hasColor();
    if (pickedColor) {
      return pickedColor?.imageGrande;
    }
    return car?.imagenGrande;
  };

  return {
    selectedPrime,
    plazo,
    cuota: getCuota(hasActivePromo),
    maintenacePrice,
    accsPrice,
    insurancePrice,
    minicuotaInsurancePrice: getDefaultMinicuotaInsurance(),
    finalPrice,
    selectedAccesories: selectedAccesories(),
    selectedMaintenance: selectedMaintenance(),
    selectedInsurance: selectedInsurance(),
    imageUrl: getImageUrl(),
    selectedBank,
    primeSecure: primeSecure(),
    carPrice: getCarPrice(),
    hasDescuentoGerencia: hasDescuentoGerencia(),
    finalPriceWithCoupon: getPriceWithCoupon(),
  };
};

export default useCar;
